import React, { useState } from 'react';
import { Card, Button, Collapse, Badge, Alert } from 'react-bootstrap';
import { FileText, ChevronDown, ChevronUp, Edit, Clock, AlertTriangle, Edit2 } from 'lucide-react';
import moment from 'moment';
import UpdateTaxProfileModal from './UpdateTaxProfileModal';

const TaxProfileCard = ({ employeeId, companyId, currentTaxProfile, history, currentContract, onUpdate }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  const handleCreateClick = () => {
    setModalMode('create');
    setShowUpdateModal(true);
  };

  const handleEditClick = () => {
    setModalMode('edit');
    setShowUpdateModal(true);
  };

  const formatDate = (date) => {
    return moment(date).format('DD MMM YYYY');
  };

  const getTaxTypeName = (taxType) => {
    switch (taxType) {
      case 'A_SKATT':
        return 'A-skatt';
      case 'SINK':
        return 'SINK-skatt';
      default:
        return taxType;
    }
  };

  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);
    if (onUpdate) onUpdate();
  };

  if (!currentTaxProfile) {
    return (
      <>
        <div className="info-section mb-4" role="region" aria-labelledby="tax-profile-heading">
          <div className="p-4">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <FileText size={20} className="me-3 text-secondary" aria-hidden="true" />
                <div>
                  <h5 className="mb-0 fw-bold" id="tax-profile-heading">Skatteprofil</h5>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                    Ingen skatteprofil registrerad
                  </p>
                </div>
              </div>
            </div>
            <div className="section-divider"></div>
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: '80px', height: '80px' }}>
                  <FileText size={40} className="text-secondary" aria-hidden="true" />
                </div>
              </div>
              <h5 className="mb-2">Ingen skatteprofil har registrerats än</h5>
              <p className="text-muted mb-4">
                Lägg till skatteuppgifter för att kunna beräkna och dra skatt korrekt vid löneutbetalning.
              </p>
              <Button
                variant="primary"
                size="lg"
                className="action-button d-flex align-items-center mx-auto"
                onClick={handleCreateClick}
                aria-label="Lägg till skatteprofil för anställd"
              >
                <Edit size={18} className="me-2" aria-hidden="true" />
                Lägg till skatteprofil nu
              </Button>
            </div>
          </div>
        </div>

        <UpdateTaxProfileModal
          show={showUpdateModal}
          handleClose={() => setShowUpdateModal(false)}
          employeeId={employeeId}
          companyId={companyId}
          currentTaxProfile={modalMode === 'edit' ? currentTaxProfile : null}
          mode={modalMode}
          onSuccess={handleUpdateSuccess}
        />
      </>
    );
  }

  return (
    <>
      <div className="info-section mb-4" role="region" aria-labelledby="tax-profile-heading">
        <div className="p-4">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <FileText size={20} className="me-3 text-secondary" aria-hidden="true" />
              <div>
                <h5 className="mb-0 fw-bold" id="tax-profile-heading">
                  {currentTaxProfile._isFuture ? 'Kommande skatteprofil' : 'Skatteprofil'}
                </h5>
                <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                  {currentTaxProfile._isFuture ? 'Börjar' : 'Gäller från'} {formatDate(currentTaxProfile.valid_from)}
                </p>
              </div>
              {currentTaxProfile._isFuture && (
                <span className="badge bg-primary ms-2" style={{ fontSize: '0.75rem' }}>
                  Kommande
                </span>
              )}
            </div>
            <Button
              variant="primary"
              className="action-button d-flex align-items-center"
              onClick={handleCreateClick}
              aria-label="Skapa ny skatteprofil för anställd"
            >
              <Edit size={16} className="me-2" aria-hidden="true" />
              Skapa ny skatteprofil
            </Button>
          </div>
          <div className="section-divider"></div>
          {/* Current Tax Profile Display */}
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera skatteprofil"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="text-muted small mb-1">Skattetyp</div>
                <div className="h5 mb-0 fw-semibold">
                  {getTaxTypeName(currentTaxProfile.tax_type)}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera skatteprofil"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="text-muted small mb-1">Skattetabell</div>
                <div className="h5 mb-0 fw-semibold">
                  {currentTaxProfile.tax_table}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera skatteprofil"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="text-muted small mb-1">Kolumn</div>
                <div className="h5 mb-0 fw-semibold">
                  {currentTaxProfile.tax_column}
                </div>
              </div>
            </div>
          </div>

          {/* Municipality Code */}
          {currentTaxProfile.municipality_code && (
            <div className="row g-2 mb-3">
              <div className="col-auto">
                <Badge bg="info" className="py-2 px-3">
                  Kommunkod: {currentTaxProfile.municipality_code}
                </Badge>
              </div>
            </div>
          )}

          {/* History Toggle */}
          {history && history.length > 1 && (
            <div className="mt-3">
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className="d-flex align-items-center p-0 text-decoration-none"
              >
                {showHistory ? <ChevronUp size={16} className="me-1" /> : <ChevronDown size={16} className="me-1" />}
                {showHistory ? 'Dölj' : 'Visa'} skattehistorik ({history.length - 1} tidigare ändringar)
              </Button>

              <Collapse in={showHistory}>
                <div className="mt-3">
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Typ</th>
                        <th>Tabell</th>
                        <th>Kolumn</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((profile) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        
                        const validFrom = new Date(profile.valid_from);
                        validFrom.setHours(0, 0, 0, 0);
                        
                        const validTo = profile.valid_to ? new Date(profile.valid_to) : null;
                        if (validTo) {
                          validTo.setHours(0, 0, 0, 0);
                        }
                        
                        // Determine status based on date ranges
                        let status = 'previous';
                        let statusLabel = 'Tidigare';
                        let statusBg = 'secondary';
                        
                        if (validFrom > today) {
                          // Future tax profile (starts after today)
                          status = 'upcoming';
                          statusLabel = 'Kommande';
                          statusBg = 'info';
                        } else if (validFrom <= today && (!validTo || validTo >= today)) {
                          // Current tax profile (started on/before today and hasn't ended yet)
                          status = 'current';
                          statusLabel = 'Aktuell';
                          statusBg = 'success';
                        }
                        
                        const isCurrent = status === 'current';
                        
                        return (
                          <tr key={profile.tax_id} className={isCurrent ? 'table-active' : ''}>
                            <td>
                              {formatDate(profile.valid_from)}
                              {profile.valid_to ? ` - ${formatDate(profile.valid_to)}` : ' - Pågående'}
                            </td>
                            <td>{getTaxTypeName(profile.tax_type)}</td>
                            <td>{profile.tax_table}</td>
                            <td>{profile.tax_column}</td>
                            <td>
                              <Badge bg={statusBg}>{statusLabel}</Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Collapse>
            </div>
          )}
        </div>
      </div>

      <UpdateTaxProfileModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        employeeId={employeeId}
        companyId={companyId}
        currentTaxProfile={modalMode === 'edit' ? currentTaxProfile : null}
        currentContract={currentContract}
        mode={modalMode}
        onSuccess={handleUpdateSuccess}
      />
    </>
  );
};

export default TaxProfileCard;
